/**
 * Generic validation utilities for form field validation
 * Used across POV application with i18n support
 * 
 * @example
 * import { createRequiredRule, createEmailRule } from '@/modules/common/utils/validationUtils';
 * 
 * emailRules() {
 *   return [createRequiredRule(this.$t), createEmailRule(this.$t)];
 * }
 */

export const MIN_LENGTH_TYPE = {
  CHARACTERS: 'common.validation_types.characters',
  DIGITS: 'common.validation_types.digits',
};

/**
 * Checks if value is not empty
 * @param {*} value - Value to check
 * @returns {boolean}
 * 
 * @example
 * isRequired('text')     // true
 * isRequired('')         // false
 * isRequired('  ')       // false
 * isRequired(null)       // false
 * 
 * // Use with createRequiredRule for form validation:
 * const rules = [createRequiredRule(this.$t)];
 */
export const isRequired = (value) => {
  if (!value && value !== 0) return false;
  return !!String(value).trim();
};

/**
 * Creates a required field validation rule
 * @param {Function} t - i18n translation function
 * @param {string} labelMessagePath - Optional i18n key for field label (uses field_required_with_label)
 * @param {string} customErrorMessagePath - Optional i18n key for complete custom error message
 * @returns {Function} Validation rule function
 * 
 * @example
 * // Basic usage - generic "Field is required"
 * createRequiredRule(this.$t)
 * 
 * @example
 * // With field label - "Email is required"
 * createRequiredRule(this.$t, 'texting_wizard.business_info.email_label')
 * 
 * @example
 * // With custom error message
 * createRequiredRule(this.$t, null, 'texting_wizard.errors.email_required_custom')
 */
export const createRequiredRule = (t, labelMessagePath = null, customErrorMessagePath = null) => (value) => {
  if (isRequired(value)) return true;
  
  if (customErrorMessagePath) {
    return t(customErrorMessagePath);
  }
  
  if (labelMessagePath) {
    return t('common.errors.field_required_with_label', { label: t(labelMessagePath) });
  }
  
  return t('common.errors.field_required');
};

/**
 * Validates email format
 * Uses whitelist approach - only allows safe characters
 * Prevents XSS and injection attacks via email input
 * @param {string} value - Email to validate
 * @returns {boolean}
 * 
 * @example
 * isValidEmail('user@example.com')        // true
 * isValidEmail('john.doe@mail.example.com')  // true (multiple dots for subdomain)
 * isValidEmail('invalid')                 // false
 * isValidEmail('user<script>@example.com') // false (blocks XSS)
 * isValidEmail('')                        // true (empty is valid - use isRequired separately)
 * 
 * // Use with createEmailRule for form validation:
 * const emailRules = [createRequiredRule(this.$t), createEmailRule(this.$t)];
 */
export const isValidEmail = (value) => {
  if (!value) return true; // Empty is valid - use createRequiredRule for required fields
  const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(value);
};

/**
 * Creates an email validation rule
 * @param {Function} t - i18n translation function
 * @param {string} customErrorMessagePath - Optional i18n key for custom error message
 * @returns {Function} Validation rule function
 * 
 * @example
 * // Basic usage - "Please enter a valid email address"
 * createEmailRule(this.$t)
 * 
 * @example
 * // With custom error message
 * createEmailRule(this.$t, 'texting_wizard.errors.email_format_invalid')
 */
export const createEmailRule = (t, customErrorMessagePath = null) => (value) => {
  if (isValidEmail(value)) return true;
  return customErrorMessagePath ? t(customErrorMessagePath) : t('common.errors.email_invalid');
};

/**
 * Validates URL format
 * Security: Blocks dangerous protocols (javascript:, data:, file:, ftp:)
 * Allows URLs with or without protocol (auto-prepends https:// for validation)
 * Requires valid TLD: 2+ alphabetic characters after final dot
 * Rejects IP addresses and localhost/intranet hostnames
 * @param {string} value - URL to validate
 * @returns {boolean}
 * 
 * @example
 * isValidUrl('https://example.com')     // true (TLD: com)
 * isValidUrl('example.com')             // true (protocol optional)
 * isValidUrl('sub.example.io')          // true (TLD: io, 2 chars)
 * isValidUrl('javascript:alert("XSS")') // false (dangerous protocol)
 * isValidUrl('data:text/html,<script>') // false (dangerous protocol)
 * isValidUrl('ftp://example.com')       // false (only http/https allowed)
 * isValidUrl('192.168.1.1')             // false (IP address - numeric TLD)
 * isValidUrl('localhost')               // false (no TLD)
 * isValidUrl('example.c')               // false (TLD only 1 char)
 * isValidUrl('')                        // true (empty is valid)
 * 
 * // Use with createUrlRule for form validation:
 * const urlRules = [createRequiredRule(this.$t), createUrlRule(this.$t)];
 */
export const isValidUrl = (value) => {
  if (!value) return true; // Empty is valid - use createRequiredRule for required fields
  
  const lowerCaseValue = value.toLowerCase();
  
  // eslint-disable-next-line no-script-url
  if (lowerCaseValue.startsWith('javascript:')
      || lowerCaseValue.startsWith('data:')
      || lowerCaseValue.startsWith('file:')
      || lowerCaseValue.startsWith('ftp:')
      || lowerCaseValue.startsWith('ftps:')) {
    return false;
  }
  
  const valueToTest = lowerCaseValue.startsWith('http://') || lowerCaseValue.startsWith('https://') 
    ? lowerCaseValue 
    : `https://${lowerCaseValue}`;
  
  try {
    const url = new URL(valueToTest);
    
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return false;
    }
    
    // Reject URLs with username/password (userinfo with @)
    if (url.username || url.password) {
      return false;
    }
    
    // Validate hostname: only safe chars (a-z, 0-9, dot, hyphen) AND ends with valid TLD (dot + 2+ letters)
    // This rejects: special chars, IPs, localhost, single-char TLDs
    return /^[a-z0-9.-]+\.[a-z]{2,}$/.test(url.hostname);
  } catch {
    return false;
  }
};

/**
 * Creates a URL validation rule
 * @param {Function} t - i18n translation function
 * @param {string} customErrorMessagePath - Optional i18n key for custom error message
 * @returns {Function} Validation rule function
 * 
 * @example
 * // Basic usage - "Please enter a valid URL"
 * createUrlRule(this.$t)
 * 
 * @example
 * // With custom error message
 * createUrlRule(this.$t, 'texting_wizard.errors.website_invalid')
 */
export const createUrlRule = (t, customErrorMessagePath = null) => (value) => {
  if (isValidUrl(value)) return true;
  return customErrorMessagePath ? t(customErrorMessagePath) : t('common.errors.url_invalid');
};

/**
 * Validates minimum length
 * @param {string} value - Value to validate
 * @param {number} minLength - Minimum required length
 * @returns {boolean}
 * 
 * @example
 * hasMinLength('12345', 5)    // true
 * hasMinLength('1234', 5)     // false
 * hasMinLength('', 5)         // true (empty is valid)
 * 
 * // Use with createMinLengthRule for form validation:
 * const postalCodeRules = [
 *   createRequiredRule(this.$t), 
 *   createMinLengthRule(this.$t, 5, MIN_LENGTH_TYPE.CHARACTERS, 'postal_code_label')
 * ];
 */
export const hasMinLength = (value, minLength) => {
  if (!value) return true; // Empty is valid - use createRequiredRule for required fields
  return String(value).length >= minLength;
};

/**
 * Creates a minimum length validation rule
 * @param {Function} t - i18n translation function
 * @param {number} minLength - Minimum required length
 * @param {string} type - Type of content (MIN_LENGTH_TYPE.CHARACTERS or MIN_LENGTH_TYPE.DIGITS)
 * @param {string} labelMessagePath - i18n key for field label (required)
 * @param {string} customErrorMessagePath - Optional i18n key for complete custom error message
 * @returns {Function} Validation rule function
 * 
 * @example
 * // Postal code - "Postal code must be at least 5 characters"
 * createMinLengthRule(this.$t, 5, MIN_LENGTH_TYPE.CHARACTERS, 'texting_wizard.business_info.postal_code_label')
 * 
 * @example
 * // Phone - "Phone number must be at least 10 digits"
 * createMinLengthRule(this.$t, 10, MIN_LENGTH_TYPE.DIGITS, 'texting_wizard.business_info.phone_label')
 * 
 * @example
 * // With custom error message
 * createMinLengthRule(this.$t, 5, MIN_LENGTH_TYPE.CHARACTERS, 'postal_code_label', 'custom.error.path')
 */
export const createMinLengthRule = (
  t,
  minLength,
  type,
  labelMessagePath,
  customErrorMessagePath = null,
) => (value) => {
  if (hasMinLength(value, minLength)) return true;
  
  if (customErrorMessagePath) {
    return t(customErrorMessagePath);
  }
  
  const typeTranslated = t(type);
  
  return t('common.errors.min_length_with_label', {
    label: t(labelMessagePath),
    minLength,
    type: typeTranslated,
  });
};
